import { useNode } from "@craftjs/core";
import React, { useEffect, useState } from "react";
import ReactPlayer from "react-player";
import styled from "styled-components";
import useApplyGlobalSettings, {
  CODEBLOCK_TYPES,
} from "../../../../hooks/useApplyGlobalSettings";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { colors } from "../../../../utils";
import { ASSETS } from "../../../../utils/config";
import TextView from "../../../TextView";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import VideoSettings from "./VideoSettings";
import { useSelector, useDispatch } from "react-redux";
import { getSelectedSegment } from "../../../../redux/selectors/segmentBuilderSelectors";
import { CircularProgress } from "@mui/material";

const VideoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative;
  height: 180px; // Set a fixed height or adjust as needed
`;

const TextContainer = styled.div`
  background-color: #344054;
  padding: 8px;
`;

const Image = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
`;

const LoadingDiv = styled.div`
  width: 100%;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Convert Loom share URLs to embed URLs
export const getVideoUrl = (url) => {
  if (!url) return url;

  // Check if it's a Loom share URL and convert to embed URL
  if (url.includes("loom.com/share/")) {
    return url.replace("/share/", "/embed/");
  }

  return url;
};

export const Video = ({
  src,
  alt,
  width,
  isApplyMarginOnAllSides,
  allSideMargin,
  margin,
  borderRadius,
  borderColor,
  borderWidth,
  showVideoTitle,
  title,
  hyperlink,
  showHyperlink,
  dropShadowColor,
  isLoading,
}) => {
  const [showControls, setShowControls] = useState(false);

  

  const videoUrl = getVideoUrl(src);

  const {
    connectors: { connect, drag },
    actions: { setProp },
  } = useNode();

  const selectedSegment = useSelector(getSelectedSegment);
  const selectedSegmentColor = selectedSegment?.settings?.color;
  const { getPaletteColor } = usePaletteColor();
  const { video: videoProps } = useSelector((state) => state.codeblockStyles);
  const dispatch = useDispatch();

  const marginStyle = isApplyMarginOnAllSides
    ? `${allSideMargin}px`
    : margin
    ? `${margin?.top}px ${margin?.right}px ${margin?.bottom}px ${margin?.left}px`
    : undefined;

  const border = borderColor
    ? `${borderWidth}px solid ${getPaletteColor(borderColor)}`
    : "none";

  // Initialize with default props from reducer for new components
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setProp((props) => {
        if (props.margin === undefined) {
          props.margin = { ...videoProps.margin };
        }
        if (props.isApplyMarginOnAllSides === undefined) {
          props.isApplyMarginOnAllSides = videoProps.isApplyMarginOnAllSides;
        }
        if (props.allSideMargin === undefined) {
          props.allSideMargin = videoProps.allSideMargin;
        }
        if (props.width === undefined) {
          props.width = videoProps.width;
        }
        if (props.borderRadius === undefined) {
          props.borderRadius = videoProps.borderRadius;
        }
        if (props.borderWidth === undefined) {
          props.borderWidth = videoProps.borderWidth;
        }
        if (props.borderColor === undefined) {
          props.borderColor = videoProps.borderColor || "suggested_color_0";
        }
        if (props.dropShadowColor === undefined) {
          props.dropShadowColor = videoProps.dropShadowColor;
        }
        if (props.showVideoTitle === undefined) {
          props.showVideoTitle = videoProps.showVideoTitle;
        }
        if (props.hyperlink === undefined) {
          props.hyperlink = videoProps.hyperlink;
        }
        if (props.showHyperlink === undefined) {
          props.showHyperlink = videoProps.showHyperlink;
        }
        if (props.title === undefined) {
          props.title = videoProps.title;
        }
      });
    }, 5);

    return () => clearTimeout(timeoutId);
  }, []);

  useApplyGlobalSettings(setProp, CODEBLOCK_TYPES.Video);

  // Check if the video is Loom and decide which component to render
  const isLoomVideo = videoUrl ? videoUrl?.includes("loom.com/embed/") : false;

  return (
    <div ref={(ref) => connect(drag(ref))}>
      <BlockIndicator>
        {isLoading ? (
          <LoadingDiv>
            <CircularProgress />
          </LoadingDiv>
        ) : src ? (
          <VideoContainer
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
            style={{
              padding: marginStyle,
            }}
          >
            <div
              style={{
                borderRadius,
                overflow: "hidden",
                border,
                cursor: showHyperlink ? "pointer" : "default",
                boxShadow:
                  dropShadowColor &&
                  `0px 0px 9.3px 0px ${getPaletteColor(dropShadowColor)}`,
                width: width || "100%", // Make sure it takes the full width of the container
                background: "transparent",
                height: "100%", // Ensure iframe fills the container
              }}
            >
              {/* Render Loom video iframe */}
              {isLoomVideo ? (
                <iframe
                  src={videoUrl}
                  frameBorder="0"
                  allowFullScreen
                  style={{
                    width: "100%",
                    height: "100%", // Make the iframe fill the container
                    borderRadius,
                  }}
                ></iframe>
              ) : (
                <ReactPlayer
                  url={videoUrl} // Use the source for all non-Loom videos
                  controls={true}
                  width="100%"
                  height="100%"
                  playing={false}
                  config={{
                    youtube: {
                      playerVars: {
                        controls: 1,
                        showinfo: 1,
                        modestbranding: 1,
                        rel: 0,
                        disablekb: 0,
                        fs: 1,
                        playsinline: 1,
                      },
                    },
                    file: {
                      attributes: {
                        frameBorder: "0",
                        allowFullScreen: true,
                      },
                    },
                  }}
                />
              )}
              {showVideoTitle && !!title && (
                <TextContainer style={{ width: "100%" }}>
                  <TextView
                    variant="largeText"
                    style={{ color: colors.white, fontWeight: 500 }}
                  >
                    {title}
                  </TextView>
                </TextContainer>
              )}
            </div>
          </VideoContainer>
        ) : (
          <Image src={ASSETS.segmentEditor.UploadVideo} />
        )}
      </BlockIndicator>
    </div>
  );
};

export const VideoDefaultProps = {
  src: null,
  alt: "New video",
  margin: undefined,
  isApplyMarginOnAllSides: undefined,
  allSideMargin: undefined,
  width: undefined,
  borderRadius: undefined,
  borderWidth: undefined,
  title: "Untitled Video",
  showVideoTitle: undefined,
  hyperlink: undefined,
  showHyperlink: undefined,
  dropShadowColor: undefined,
  borderColor: undefined,
  isLoading: false,
};

Video.craft = {
  props: VideoDefaultProps,
  related: {
    settings: VideoSettings,
  },
};
